import SelectBranchModal from "@/components/cases/select-branch-modal";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/core/actions/dropdown-menu";
import Badge from "@/components/core/badge.tsx";
import { Blanket, BlanketImage, BlanketInfo } from "@/components/core/blanket";
import { Button } from "@/components/core/buttons/button";
import { Form } from "@/components/core/forms/form.tsx";
import ImageSelect from "@/components/core/forms/image-select";
import { useModal } from "@/components/core/modals/use-modal";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/core/tabs.tsx";
import { Content } from "@/components/layouts/content";
import { Header } from "@/components/layouts/header";
import { useUseRemoveRoleMutation, useUserRemoveBranchMutation, userQueryOptions } from "@/features/dashboard/api/queries/user-queries";
import RoleModal from "@/features/dashboard/components/role-modal";
import type { Role } from "@/features/dashboard/types/role";
import { formatDate } from "@/utils/format-date";
import { IconAlertTriangle, IconDots, IconFolderExclamation, IconPlus } from "@tabler/icons-react";
import { useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { Controller, useForm } from "react-hook-form";

export const Route = createFileRoute("/_dashboard/users_/$userId")({
	loader: ({ context: { queryClient }, params }) => {
		return queryClient.ensureQueryData(userQueryOptions(params.userId));
	},
	component: UserShow,
});

function UserShow() {
	const params = Route.useParams();
	const userQuery = useSuspenseQuery(userQueryOptions(params.userId));
	const data = userQuery.data;
	console.log(data);
	const modal = useModal();

	const form = useForm({
		defaultValues: {
			id: "",
			avatar: "",
			username: "",
			password: "",
			description: "",
			attachment: "",
			selectbox: "",
			combobox: "",
			tagsinput: "",
			datepicker: "",
		},
	});

	const userRemovePermissionMutation = useUseRemoveRoleMutation();
	const userRemoveBranchMutation = useUserRemoveBranchMutation();
	const handleRemoveRole = (role: Role) => {
		modal.openModal({
			title: "Remove Role",
			description: "You are revoking this role. Please confirm.",
			type: "confirm",
			variant: "green",
			onConfirm: async () => {
				await userRemovePermissionMutation.mutateAsync({
					role_id: role.id,
					user_id: data.id,
				});
			},
		});
	};
	const handleRemoveBranch = (branch: any) => {
		console.log(branch, "branch");
		modal.openModal({
			title: "Remove Branch",
			description: "You are revoking this branch. Please confirm.",
			type: "confirm",
			variant: "green",
			onConfirm: async () => {
				await userRemoveBranchMutation.mutateAsync({
					court_branch_id: branch.assignable_id,
					user_id: data.id,
				});
			},
		});
	};

	const handleAddRole = () => {
		modal.openModal({
			title: "Add Role",
			component: <RoleModal data={data} />,
		});
	};

	const handleAddBranch = () => {
		modal.openModal({
			title: "Add Branch",
			component: <SelectBranchModal data={data} />,
		});
	};

	return (
		<Content>
			<Header />
			<div className="flex flex-col w-full max-w-base p-6 gap-6">
				<Form {...form}>
					<form className="w-full">
						<Tabs orientation="vertical" defaultValue="details">
							<TabsList>
								<TabsTrigger value="details">Details</TabsTrigger>
								<TabsTrigger value="roles">Roles</TabsTrigger>
								<TabsTrigger value="branches">Branches</TabsTrigger>
								<TabsTrigger value="sessions">Sessions</TabsTrigger>
								<TabsTrigger value="logs">Logs</TabsTrigger>
							</TabsList>
							<TabsContent value="details">
								<div className="flex flex-col w-full gap-6">
									<div className="flex flex-row items-center justify-between w-full gap-4">
										<div className="inline-flex flex-col w-auto">
											<span className="text-xl text-foreground-dark font-semibold">User Details</span>
											<span className="text-base text-foreground/70">Brief description for help here</span>
										</div>
									</div>
									<div className="grid grid-cols-12 w-full gap-2">
										<div className="col-span-12 flex flex-col w-full">
											<Controller
												render={({ field }) => (
													<ImageSelect
														initials="AB"
														avatar="https://example.com/avatar_thumbnail.jpg"
														onImageRemove={async () => {
															field.onChange("");
														}}
														onCrop={(avatar) => {
															field.onChange(avatar);
														}}
													/>
												)}
												control={form.control}
												name="avatar"
											/>
										</div>
									</div>
									<table className="w-full text-base">
										<tbody>
											<tr className="border-b last:border-b-0 border-border/50">
												<td className="w-[1%] whitespace-nowrap p-3 bg-slate-50 font-semibold text-foreground-dark">Status</td>
												<td className="p-3 text-foreground">
													<Badge size="sm" color="green-light">
														{data.status || "Active"}
													</Badge>
												</td>
											</tr>
											<tr className="border-b last:border-b-0 border-border/50">
												<td className="w-[1%] whitespace-nowrap p-3 bg-slate-50 font-semibold text-foreground-dark">Name</td>
												<td className="p-3 text-foreground">{data.full_name}</td>
											</tr>
											<tr className="border-b last:border-b-0 border-border/50">
												<td className="w-[1%] whitespace-nowrap p-3 bg-slate-50 font-semibold text-foreground-dark">Email</td>
												<td className="p-3 text-foreground">{data.email}</td>
											</tr>
											<tr className="border-b last:border-b-0 border-border/50">
												<td className="w-[1%] whitespace-nowrap p-3 bg-slate-50 font-semibold text-foreground-dark">Last Login</td>
												<td className="p-3 text-foreground">{formatDate(data.last_login_at || "")}</td>
											</tr>
											<tr className="border-b last:border-b-0 border-border/50">
												<td className="w-[1%] whitespace-nowrap p-3 bg-slate-50 font-semibold text-foreground-dark">Date Created</td>
												<td className="p-3 text-foreground">{formatDate(data?.created_at || "-")}</td>
											</tr>
										</tbody>
									</table>
								</div>
							</TabsContent>
							<TabsContent value="roles">
								<Button onClick={handleAddRole} color="gray" type="button" size="sm">
									<IconPlus size={16} stroke={2} />
									Add Role
								</Button>

								{data.roles.length > 0 ? (
									<table className="w-full mt-2">
										<thead>
											<tr className="border-b-2 border-slate-200">
												<th className="text-left p-2 text-xs text-gray-800 w-[400px]">Role / Description</th>
												<th className="text-left p-2 text-xs text-gray-800">Created</th>
												<th className="text-right p-2 text-xs text-gray-800" />
											</tr>
										</thead>
										<tbody>
											{data.roles.map((role) => (
												<tr key={role.id} className="border-b border-slate-200 last:border-b-0">
													<td className="text-left p-2 text-sm text-gray-600">
														<div className="flex flex-row items-center w-auto gap-2">
															{role.organization && (
																<Avatar className="flex self-start justify-center items-center w-[32px] h-[32px] rounded-full bg-slate-300">
																	<AvatarFallback>{role.organization.initials}</AvatarFallback>
																	<AvatarImage src={role.organization.avatar_url} />
																</Avatar>
															)}
															<div className="flex flex-col w-auto gap-1">
																<span className="leading-3 text-xs font-semibold text-blue-600 hover:text-blue-500">{role.name}</span>
																<span className="leading-3 text-xs text-gray-400">{role.description}</span>
															</div>
														</div>
													</td>
													<td className="text-left p-2 text-xs text-gray-600">{formatDate(role.created_at)}</td>
													<td className="text-right p-2 text-xs text-gray-600">
														<div className="flex flex-row justify-end">
															<DropdownMenu modal={false}>
																<DropdownMenuTrigger asChild>
																	<Button className="translate-y-[1px]" mode="icon-circle" size="xs" variant="light">
																		<IconDots size={16} stroke={1.5} />
																	</Button>
																</DropdownMenuTrigger>
																<DropdownMenuContent>
																	<DropdownMenuItem onSelect={() => handleRemoveRole(role)} dangerZone>
																		<div className="flex flex-row items-center gap-1">
																			<IconAlertTriangle size={16} stroke={2} />
																			<span className="translate-y-[-1px] leading-1">Revoke Role</span>
																		</div>
																	</DropdownMenuItem>
																</DropdownMenuContent>
															</DropdownMenu>
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								) : (
									<Blanket width="sm">
										<BlanketImage icon={<IconFolderExclamation size={30} stroke={1.5} />} />
										<BlanketInfo
											title="No data found"
											message="Looks like you are looking for something non-existent. Be the first to add data for this page."
											action={
												<Button onClick={handleAddRole} color="gray" type="button">
													<IconPlus size={16} stroke={2} />
													Add Role
												</Button>
											}
										/>
									</Blanket>
								)}
							</TabsContent>
							<TabsContent value="branches">
								<Button onClick={handleAddBranch} color="gray" type="button" size="sm">
									<IconPlus size={16} stroke={2} />
									Add Branch
								</Button>

								{data.branches?.length > 0 ? (
									<table className="w-full mt-2">
										<thead>
											<tr className="border-b-2 border-slate-200">
												<th className="text-left p-2 text-xs text-gray-800 w-[400px]">Branch</th>
												<th className="text-left p-2 text-xs text-gray-800">Created</th>
												<th className="text-right p-2 text-xs text-gray-800" />
											</tr>
										</thead>
										<tbody>
											{data.branches?.map((branch) => (
												<tr key={branch.id} className="border-b border-slate-200 last:border-b-0">
													<td className="text-left p-2 text-sm text-gray-600">
														<div className="flex flex-row items-center w-auto gap-2">
															<div className="flex flex-col w-auto gap-1">
																<span className="leading-3 text-xs font-semibold text-blue-600 hover:text-blue-500">{branch?.assignable?.formatted_name}</span>
															</div>
														</div>
													</td>
													<td className="text-left p-2 text-xs text-gray-600">{formatDate(branch?.created_at)}</td>
													<td className="text-right p-2 text-xs text-gray-600">
														<div className="flex flex-row justify-end">
															<DropdownMenu modal={false}>
																<DropdownMenuTrigger asChild>
																	<Button className="translate-y-[1px]" mode="icon-circle" size="xs" variant="light">
																		<IconDots size={16} stroke={1.5} />
																	</Button>
																</DropdownMenuTrigger>
																<DropdownMenuContent>
																	<DropdownMenuItem onSelect={() => handleRemoveBranch(branch)} dangerZone>
																		<div className="flex flex-row items-center gap-1">
																			<IconAlertTriangle size={16} stroke={2} />
																			<span className="translate-y-[-1px] leading-1">Revoke Branch</span>
																		</div>
																	</DropdownMenuItem>
																</DropdownMenuContent>
															</DropdownMenu>
														</div>
													</td>
												</tr>
											))}
										</tbody>
									</table>
								) : (
									<Blanket width="sm">
										<BlanketImage icon={<IconFolderExclamation size={30} stroke={1.5} />} />
										<BlanketInfo
											title="No data found"
											message="Looks like you are looking for something non-existent. Be the first to add data for this page."
											action={
												<Button onClick={handleAddBranch} color="gray" type="button">
													<IconPlus size={16} stroke={2} />
													Add Branch
												</Button>
											}
										/>
									</Blanket>
								)}
							</TabsContent>
							<TabsContent value="sessions">
								<div className="flex flex-col w-full gap-6">
									<div className="flex flex-row items-center justify-between w-full gap-4">
										<div className="inline-flex flex-col w-auto">
											<span className="text-xl text-foreground-dark font-semibold">User Sessions</span>
											<span className="text-base text-foreground/70">Brief description for help here</span>
										</div>
									</div>
									<Blanket width="sm">
										<BlanketImage icon={<IconFolderExclamation size={30} stroke={1.5} />} />
										<BlanketInfo
											title="No data found"
											message="Looks like you are looking for something non-existent. Be the first to add data for this page."
										/>
									</Blanket>
								</div>
							</TabsContent>
							<TabsContent value="logs">
								<div className="flex flex-col w-full gap-6">
									<div className="flex flex-row items-center justify-between w-full gap-4">
										<div className="inline-flex flex-col w-auto">
											<span className="text-xl text-foreground-dark font-semibold">User Logs</span>
											<span className="text-base text-foreground/70">Brief description for help here</span>
										</div>
									</div>
									<Blanket width="sm">
										<BlanketImage icon={<IconFolderExclamation size={30} stroke={1.5} />} />
										<BlanketInfo
											title="No data found"
											message="Looks like you are looking for something non-existent. Be the first to add data for this page."
										/>
									</Blanket>
								</div>
							</TabsContent>
						</Tabs>
					</form>
				</Form>
			</div>
		</Content>
	);
}
